/**
 * CUSTOMER-R4.15-K13 — sanitized diagnostic envelope for customer-submitted BUG reports.
 * Never includes initData, tokens, IBAN, purpose, card data, or ord_*.
 */

import {
  getObservabilitySnapshot,
  sanitizeObservabilityText,
  type ObservabilitySnapshot,
} from "./observability-ring-v2.js";

export type CustomerFeedbackType = "BUG" | "IMPROVEMENT";

export type FeedbackDiagnosticEnvelope = {
  feedback_kind: CustomerFeedbackType;
  timestamp: string;
  route: string;
  route_params_sanitized: Record<string, string>;
  source_route: string | null;
  build_id: string | null;
  telegram_platform: string | null;
  os_browser: string | null;
  viewport: { w: number; h: number } | null;
  network_online: boolean | null;
  public_order_number: string | null;
  product_id: string | null;
  category_id: string | null;
  last_route_load_ms: number | null;
  last_api_error: ObservabilitySnapshot["last_api_error"];
  last_error: ObservabilitySnapshot["last_error"];
  correlation_id: string;
  recent_actions: ObservabilitySnapshot["recent_actions"];
  sort: string | null;
  search_query: string | null;
  scroll_y: number | null;
  session_id: string;
  navigation_id: string;
};

const FORBIDDEN_IN_JSON = /(initdata|authorization|bearer |iban|призначення|purpose|rnokpp|card_number|cvv|api[_-]?key|private[_-]?key|ord_)/i;

function platformInfo(): { telegram_platform: string | null; os_browser: string | null } {
  try {
    const wa = (window as unknown as { Telegram?: { WebApp?: { platform?: string } } }).Telegram?.WebApp;
    const platform = typeof wa?.platform === "string" ? wa.platform.slice(0, 32) : null;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : "";
    const os =
      /Android/i.test(ua) ? "Android" : /iPhone|iPad/i.test(ua) ? "iOS" : /Windows/i.test(ua) ? "Windows" : "other";
    return {
      telegram_platform: platform ? `Telegram ${platform}` : "Telegram WebView",
      os_browser: os,
    };
  } catch {
    return { telegram_platform: null, os_browser: null };
  }
}

export function buildFeedbackDiagnosticEnvelope(
  kind: CustomerFeedbackType,
  extraParams: Record<string, string | null | undefined> = {},
): FeedbackDiagnosticEnvelope {
  const snap = getObservabilitySnapshot();
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(extraParams)) {
    if (FORBIDDEN_IN_JSON.test(k) || FORBIDDEN_IN_JSON.test(String(v ?? ""))) continue;
    const clean = sanitizeObservabilityText(v, 80);
    if (clean) params[k.slice(0, 40)] = clean;
  }
  const plat = platformInfo();
  let viewport: { w: number; h: number } | null = null;
  try {
    viewport = { w: Math.round(window.innerWidth), h: Math.round(window.innerHeight) };
  } catch {
    viewport = null;
  }
  let online: boolean | null = null;
  try {
    online = navigator.onLine;
  } catch {
    online = null;
  }
  return {
    feedback_kind: kind,
    timestamp: new Date().toISOString(),
    route: snap.route,
    route_params_sanitized: params,
    source_route: snap.source_route,
    build_id: snap.build_id,
    telegram_platform: plat.telegram_platform,
    os_browser: plat.os_browser,
    viewport,
    network_online: online,
    public_order_number: snap.public_order_number,
    product_id: snap.product_id,
    category_id: snap.category_id,
    last_route_load_ms: snap.last_route_load_ms,
    last_api_error: snap.last_api_error,
    last_error: snap.last_error,
    correlation_id: snap.correlation_id,
    recent_actions: snap.recent_actions,
    sort: snap.sort,
    search_query: snap.search_query,
    scroll_y: snap.scroll_y,
    session_id: snap.session_id,
    navigation_id: snap.navigation_id,
  };
}

export function assertEnvelopeHasNoSecrets(envelope: FeedbackDiagnosticEnvelope): boolean {
  const blob = JSON.stringify(envelope);
  return !FORBIDDEN_IN_JSON.test(blob);
}

type FeedbackOpenListener = (opts: { type: CustomerFeedbackType | "chooser" }) => void;
const listeners = new Set<FeedbackOpenListener>();

export function subscribeCustomerFeedbackOpen(fn: FeedbackOpenListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function requestCustomerFeedback(type: CustomerFeedbackType | "chooser" = "chooser"): void {
  for (const fn of listeners) {
    try {
      fn({ type });
    } catch {
      /* never break UX */
    }
  }
}
